import { supabase } from "@/lib/supabase";
import type { Json } from "@/lib/database.types";

// Types for lesson performance
export type LessonPerformance = {
  topicSlug: string;
  topicTitle: string | null;
  unitId: string | null;
  correctCount: number;
  totalCount: number;
  percentage: number;
  masteryProbability: number; // BKT P(L) - probability of mastery
  lastAttemptAt: string | null;
  history: HistoryEntry[];
};

export type HistoryEntry = {
  date: string;
  correct: number;
  total: number;
};

// Types for personalization preferences stored in preferences table
export type SubjectPersonalization = {
  targetGrade: string;
  lastUpdated: string;
};

export type PersonalizationPreferences = {
  personalization?: Record<string, SubjectPersonalization>;
};

/**
 * Fetch lesson performance for a user and subject from the aggregated table
 */
export async function fetchLessonPerformance(
  userId: string,
  subjectId: string
): Promise<LessonPerformance[]> {
  const { data, error } = await supabase.rpc("get_lesson_performance", {
    p_user_id: userId,
    p_subject_id: subjectId,
  });

  if (error) throw error;

  return (data ?? []).map((row: {
    topic_slug: string;
    topic_title: string | null;
    unit_id: string | null;
    correct_count: number;
    total_count: number;
    percentage: number;
    mastery_probability: number;
    last_attempt_at: string | null;
    history: Json;
  }) => ({
    topicSlug: row.topic_slug,
    topicTitle: row.topic_title,
    unitId: row.unit_id,
    correctCount: row.correct_count,
    totalCount: row.total_count,
    percentage: row.percentage,
    masteryProbability: row.mastery_probability,
    lastAttemptAt: row.last_attempt_at,
    history: parseHistory(row.history),
  }));
}

function parseHistory(history: Json): HistoryEntry[] {
  if (!history || !Array.isArray(history)) return [];
  return history.map((entry) => {
    const e = entry as Record<string, unknown>;
    return {
      date: String(e.date ?? ""),
      correct: Number(e.correct ?? 0),
      total: Number(e.total ?? 0),
    };
  });
}

/**
 * Get personalization preferences for a specific subject
 */
export async function getSubjectPersonalization(
  userId: string,
  subjectId: string
): Promise<SubjectPersonalization | null> {
  const { data, error } = await supabase
    .from("preferences")
    .select("data")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  if (!data?.data) return null;

  const prefs = data.data as PersonalizationPreferences;
  return prefs.personalization?.[subjectId] ?? null;
}

/**
 * Save personalization preferences for a specific subject
 * Uses atomic RPC to prevent race conditions from concurrent updates
 */
export async function saveSubjectPersonalization(
  userId: string,
  subjectId: string,
  settings: SubjectPersonalization
): Promise<void> {
  // Use atomic RPC for race-condition-free update
  const { error } = await supabase.rpc("upsert_subject_personalization", {
    p_user_id: userId,
    p_subject_id: subjectId,
    p_settings: settings as unknown as Json,
  });

  if (error) throw error;
}

/**
 * Calculate predicted grade based on average BKT mastery probability
 * Uses standard AP exam thresholds mapped to mastery probability
 */
export function calculatePredictedGrade(averageMastery: number): string {
  if (averageMastery >= 0.9) return "5";
  if (averageMastery >= 0.75) return "4";
  if (averageMastery >= 0.6) return "3";
  if (averageMastery >= 0.45) return "2";
  return "1";
}

/**
 * Get recommended study order based on lesson performance
 * Returns lessons sorted by priority: lowest mastery first
 */
export function getRecommendedStudyOrder(lessons: LessonPerformance[]): LessonPerformance[] {
  return [...lessons].sort((a, b) => {
    // Primary sort: by mastery probability (weakest first)
    const masteryDiff = a.masteryProbability - b.masteryProbability;
    if (Math.abs(masteryDiff) >= 0.1) return masteryDiff;

    // Secondary sort: prioritize lessons with more attempts (more data = more reliable)
    const attemptsDiff = b.totalCount - a.totalCount;
    if (attemptsDiff !== 0) return attemptsDiff;

    // Tertiary sort: most recently attempted first (fresher in memory)
    const aTime = a.lastAttemptAt ? new Date(a.lastAttemptAt).getTime() : 0;
    const bTime = b.lastAttemptAt ? new Date(b.lastAttemptAt).getTime() : 0;
    return bTime - aTime;
  });
}

/**
 * Calculate overall statistics from lesson performance data
 * Now includes average mastery probability from BKT
 */
export function calculateOverallStats(lessons: LessonPerformance[]): {
  totalCorrect: number;
  totalAttempts: number;
  overallPercentage: number;
  averageMastery: number;
} {
  const totalCorrect = lessons.reduce((sum, l) => sum + l.correctCount, 0);
  const totalAttempts = lessons.reduce((sum, l) => sum + l.totalCount, 0);
  const overallPercentage = totalAttempts > 0 
    ? Math.round((totalCorrect / totalAttempts) * 100) 
    : 0;
  
  // Calculate average mastery across all practiced lessons
  const averageMastery = lessons.length > 0
    ? lessons.reduce((sum, l) => sum + l.masteryProbability, 0) / lessons.length
    : 0;

  return { totalCorrect, totalAttempts, overallPercentage, averageMastery };
}

/**
 * Calculate progress trend for a lesson based on history
 * Returns: "improving", "declining", "stable", or "unknown"
 */
export function calculateProgressTrend(
  history: HistoryEntry[]
): "improving" | "declining" | "stable" | "unknown" {
  if (history.length < 2) return "unknown";

  // Take last 5 entries or all if less
  const recent = history.slice(-5);
  if (recent.length < 2) return "unknown";

  // Calculate rolling percentages
  const percentages = recent.map((entry) =>
    entry.total > 0 ? (entry.correct / entry.total) * 100 : 0
  );

  // Compare first half to second half
  const midpoint = Math.floor(percentages.length / 2);
  const firstHalfAvg =
    percentages.slice(0, midpoint).reduce((a, b) => a + b, 0) / midpoint;
  const secondHalfAvg =
    percentages.slice(midpoint).reduce((a, b) => a + b, 0) /
    (percentages.length - midpoint);

  const diff = secondHalfAvg - firstHalfAvg;

  if (diff >= 10) return "improving";
  if (diff <= -10) return "declining";
  return "stable";
}

/**
 * Get progress data for chart visualization
 * Aggregates all lesson history into daily totals
 */
export function getProgressChartData(
  lessons: LessonPerformance[]
): Array<{ date: string; correct: number; total: number; percentage: number }> {
  const dailyMap = new Map<string, { correct: number; total: number }>();

  lessons.forEach((lesson) => {
    lesson.history.forEach((entry) => {
      const existing = dailyMap.get(entry.date) ?? { correct: 0, total: 0 };
      existing.correct += entry.correct;
      existing.total += entry.total;
      dailyMap.set(entry.date, existing);
    });
  });

  return Array.from(dailyMap.entries())
    .map(([date, data]) => ({
      date,
      correct: data.correct,
      total: data.total,
      percentage: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export type SubjectPersonalizationSummary = {
  subjectId: string;
  predictedGrade: string | null;
  targetGrade: string | null;
  averageMastery: number;
  hasData: boolean;
};

/**
 * Fetch personalization summary for all user subjects
 * Returns predicted/target grades for each subject
 */
export async function fetchPersonalizationSummary(
  userId: string,
  subjectIds: string[]
): Promise<Map<string, SubjectPersonalizationSummary>> {
  const result = new Map<string, SubjectPersonalizationSummary>();

  // Fetch all lesson performance data in parallel
  const performancePromises = subjectIds.map(async (subjectId) => {
    try {
      const lessons = await fetchLessonPerformance(userId, subjectId);
      const stats = calculateOverallStats(lessons);
      const predictedGrade = lessons.length > 0 ? calculatePredictedGrade(stats.averageMastery) : null;
      return { subjectId, lessons, predictedGrade, averageMastery: stats.averageMastery };
    } catch {
      return { subjectId, lessons: [], predictedGrade: null, averageMastery: 0 };
    }
  });

  // Fetch all preferences once
  const prefsPromise = supabase
    .from("preferences")
    .select("data")
    .eq("user_id", userId)
    .maybeSingle();

  const [performanceResults, prefsResult] = await Promise.all([
    Promise.all(performancePromises),
    prefsPromise,
  ]);

  if (prefsResult.error) {
    console.warn("Failed to fetch preferences:", prefsResult.error);
  }

  const prefs = (prefsResult.data?.data as PersonalizationPreferences) ?? {};

  // Build summary for each subject
  performanceResults.forEach(({ subjectId, lessons, predictedGrade, averageMastery }) => {
    const targetGrade = prefs.personalization?.[subjectId]?.targetGrade ?? null;
    result.set(subjectId, {
      subjectId,
      predictedGrade,
      targetGrade,
      averageMastery,
      hasData: lessons.length > 0,
    });
  });

  return result;
}
